import { useEffect, useState } from 'react';
import { X, Users, TrendingUp, MapPin } from 'lucide-react';
import { fetchElectionResults, fetchVoterStatistics } from '@/lib/api';
import type { Constituency, Election, ElectionResult, VoterStatistics } from '@/lib/types';
import { formatNumber, formatPercentage, getPartyColor } from '@/lib/utils';

interface ConstituencyModalProps {
  constituency: Constituency;
  selectedElection: Election | null;
  onClose: () => void;
}

export function ConstituencyModal({
  constituency,
  selectedElection,
  onClose,
}: ConstituencyModalProps) {
  const [results, setResults] = useState<ElectionResult[]>([]);
  const [statistics, setStatistics] = useState<VoterStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!selectedElection) return;

    setIsLoading(true);
    Promise.all([
      fetchElectionResults(constituency.id, selectedElection.id),
      fetchVoterStatistics(constituency.id, selectedElection.id),
    ])
      .then(([resultsData, statsData]) => {
        setResults(resultsData);
        setStatistics(statsData[0] || null);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [constituency.id, selectedElection?.id]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{constituency.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{constituency.code}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {statistics && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-accent/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium">Total Voters</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {formatNumber(statistics.totalVoters)}
                    </p>
                  </div>

                  <div className="bg-accent/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium">Turnout</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {formatPercentage(statistics.turnoutPercentage)}
                    </p>
                  </div>

                  <div className="bg-accent/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-xs font-medium">Valid Votes</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {formatNumber(statistics.validVotes)}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Election Results - {selectedElection?.yearBs} BS
                </h3>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Rank
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Candidate
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Party
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                          Votes
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                          %
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          className={result.isWinner ? 'bg-green-50' : ''}
                        >
                          <td className="px-4 py-3 text-sm">
                            {result.rank}
                            {result.isWinner && (
                              <span className="ml-2 text-xs font-semibold text-green-600">
                                ✓ Winner
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {result.candidateName}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getPartyColor(result.partyCode) }}
                              />
                              {result.partyName}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            {formatNumber(result.votes)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {formatPercentage(result.votePercentage)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
